import { useEffect, useState, type ReactNode } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useRepoContext } from '../contexts/RepoContext';
import { apiConfig } from '../authConfig';
import { AISearchModal } from './AISearchModal';
import { UserSettingsModal } from './UserSettingsModal';

interface Props {
    nav: ReactNode;
    main: ReactNode;
    aside?: ReactNode;
    asideRequestOpen?: number;
    onOpenDoc: (repoId: string, path: string) => void;
    navOpen: boolean;
    asideOpen: boolean;
    onSetAsideOpen: (v: boolean) => void;
}

export function AppShell({ nav, main, aside, asideRequestOpen, onOpenDoc, navOpen, asideOpen, onSetAsideOpen }: Props) {
    const { userInfo, logout } = useAuth();
    const { allowedRepos } = useRepoContext();
    const isAdmin = userInfo?.role === 'admin';
    const [searchOpen, setSearchOpen] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);

    useEffect(() => {
        if (typeof asideRequestOpen === 'number') onSetAsideOpen(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [asideRequestOpen]);

    // Show the org slug once next to the brand, from any registered repo. All
    // repos in this tenant share one install/org so the first one is fine.
    const orgSlug = allowedRepos?.[0]?.owner ?? null;

    const showAside = !!aside && asideOpen;
    const showNav = navOpen;

    return (
        <div className="app-shell">
            <header className="app-header">
                <div className="app-header__brand">
                    <img src="/logo.png" alt="" className="app-header__logo" />
                    <strong>{apiConfig.tenantDisplayName}</strong>
                    <span className="app-header__product">docreview</span>
                    {orgSlug && <span className="app-header__org" title={orgSlug}>· {orgSlug}</span>}
                </div>
                <div className="app-header__search">
                    <button
                        type="button"
                        className="ask-ai-btn"
                        onClick={() => setSearchOpen(true)}
                        title="Ask AI"
                        aria-label="Ask AI"
                    >
                        <span aria-hidden="true">✨</span>
                    </button>
                </div>
                <div className="app-header__user">
                    {userInfo && (
                        <>
                            <span className="app-header__email" title={userInfo.email}>
                                {userInfo.displayName ?? userInfo.email}
                            </span>
                            {isAdmin && <span className="badge badge--admin">admin</span>}
                            <button
                                type="button"
                                className="btn btn--ghost btn--small"
                                onClick={() => setSettingsOpen(true)}
                                title="Settings"
                                aria-label="Settings"
                            >
                                ⚙
                            </button>
                            <button className="btn btn--ghost" onClick={() => void logout()}>
                                Sign out
                            </button>
                        </>
                    )}
                </div>
            </header>
            <div
                className={
                    'app-body' +
                    (showNav ? '' : ' app-body--nav-collapsed') +
                    (showAside ? ' app-body--has-aside' : '')
                }
            >
                {showNav && <aside className="app-nav">{nav}</aside>}
                <main className="app-main">{main}</main>
                {showAside && <aside className="app-aside">{aside}</aside>}
            </div>
            {searchOpen && (
                <AISearchModal
                    onClose={() => setSearchOpen(false)}
                    onOpenDoc={(repoId, path) => onOpenDoc(repoId, path)}
                />
            )}
            {settingsOpen && <UserSettingsModal onClose={() => setSettingsOpen(false)} />}
        </div>
    );
}
